import React, { useEffect, useState } from "react";
import { Menu, X, Volume2, VolumeX } from "lucide-react";
import { ASSETS, NAV_LINKS } from "../mock";
import { useMusic } from "../context/MusicContext";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { playing, toggle, volume, setVolume } = useMusic();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkColor = scrolled ? "text-[#0B284A]/80" : "text-[#0B284A]/80";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 w-full z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#FAF8F3]/85 backdrop-blur-xl border-b border-[#B88A3B]/30" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <a href="#hero" data-testid="header-logo" className="flex items-center group">
          <img
            alt="Marbella Consulting"
            className="h-14 sm:h-16 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] group-hover:opacity-90 transition-opacity duration-300"
            src={ASSETS.logo}
          />
        </a>
        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className={`text-sm uppercase tracking-[0.2em] transition-colors duration-300 hover:text-[#B88A3B] ${linkColor}`}
            >
              {l.label}
            </a>
          ))}
          <div className="flex items-center gap-2.5">
            <button
              onClick={toggle}
              data-testid="music-toggle"
              aria-label={playing ? "Pause background music" : "Play background music"}
              title={playing ? "Pause music" : "Play calm music"}
              className={`w-10 h-10 rounded-full flex items-center justify-center border transition-colors duration-300 ${
                playing
                  ? "bg-[#B88A3B] border-[#B88A3B] text-white"
                  : "border-[#B88A3B]/50 text-[#B88A3B] hover:border-[#B88A3B]"
              }`}
            >
              {playing ? <Volume2 size={17} /> : <VolumeX size={17} />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              data-testid="volume-slider"
              aria-label="Music volume"
              className="music-range w-20 h-1 cursor-pointer"
              style={{
                background: `linear-gradient(to right, #B88A3B ${volume * 100}%, rgba(184,138,59,0.25) ${volume * 100}%)`,
              }}
            />
          </div>
          <a
            href="#contact"
            className="bg-[#B88A3B] text-white px-6 py-2.5 text-sm uppercase tracking-[0.2em] font-medium hover:bg-[#C09245] transition-colors duration-300"
          >
            Enquire
          </a>
        </nav>
        <div className="flex items-center gap-3 lg:hidden">
          <button
            onClick={toggle}
            data-testid="music-toggle-mobile"
            aria-label={playing ? "Pause background music" : "Play background music"}
            className={`w-10 h-10 rounded-full flex items-center justify-center border transition-colors duration-300 ${
              playing
                ? "bg-[#B88A3B] border-[#B88A3B] text-white"
                : "border-[#B88A3B]/50 text-[#B88A3B]"
            }`}
          >
            {playing ? <Volume2 size={17} /> : <VolumeX size={17} />}
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            className="text-[#B88A3B]"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FAF8F3] border-t border-[#B88A3B]/20 px-6 py-6 flex flex-col gap-5">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-sm uppercase tracking-[0.2em] text-[#0B284A]/80 hover:text-[#B88A3B] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            className="bg-[#B88A3B] text-white px-6 py-3 text-sm uppercase tracking-[0.2em] font-medium text-center hover:bg-[#C09245] transition-colors"
          >
            Enquire
          </a>
        </div>
      )}
    </header>
  );
};

export default Header;
